import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import Cookies from 'universal-cookie';
import styled, { createGlobalStyle } from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { OverlayId } from '../Overlays/types';
import StateInterface from '../../redux/types';
import useConfig from '../../hooks/useConfig';
import useGa from '../../hooks/useGa';
import { categoryActions } from '../../redux/category/actions';
import useFetchingAction from '../../hooks/useFetchingAction/useFetchingAction';
import { VictorQaClasses } from '../../../common/qaIds';
import { TrackHeaderActions } from '../../../common/ga/types';
import EventListener, { createHeaderEvents } from '../../../common/events';
import { getHeaderBarBannerActionsIfNeeded } from '../../redux/headerBarBanner/actions';
import { useOverlayStateChecker } from '../Overlays/useOverlayStateChecker';
import { combineThunks } from '../../redux/utils';
import { useOverlayUrlService } from '../Overlays/useOverlay';
import OneHeader from './OneHeader';
import OneHeaderContainer from './OneHeaderContainer';
import { useShowSmartBanner } from './SmartBanner/useSmartBanner';
import { useLayoutByPage } from 'Client/hooks/useLayoutByPage';

const BurgerFlyout = loadable(() => import('./Overlays/BurgerFlyout'));
const DetachableNavBar = loadable(() => import('./NavigationBar/DetachableNavBar'));
const DetachableBannerBar = loadable(() => import('./BarBanner/DetachableBarBanner'));

const StyledShadow = styled.div<{ smartBanner: boolean; isSticky: boolean }>`
    display: block;
    background-image: linear-gradient(rgba(0, 0, 0, 0.1), transparent);
    height: 2px;
    left: 0;
    position: ${({ isSticky }) => (isSticky ? 'sticky' : 'static')};
    z-index: ${({ theme }) => theme.zindexOneHeaderShadow};

    top: ${({ theme, smartBanner }) =>
        smartBanner
            ? `${theme.smartBannerHeight + theme.oneHeaderModuleHeight}px`
            : `${theme.oneHeaderModuleHeight}px`};
    ${forDesktop`
        display: none;
    `}
`;

const OneHeaderContainerNonStickyStyle = createGlobalStyle`
    #wwOneHeader {
        position: unset;
    }
`;

const ConnectedOneHeader: React.FC<{ detachedHeaderMode: boolean }> = ({ detachedHeaderMode }) => {
    const { pushOverlay } = useOverlayUrlService();
    const minOrderValue = useSelector<StateInterface, number | null>(
        state => state.configurations.shipping.minOrderValue
    );
    const wishListCount = useSelector<StateInterface, number>(state => state.wishlist.count);
    const overlayStateChecker = useOverlayStateChecker();
    const BurgerMenuOverlay = overlayStateChecker(OverlayId.BURGER_MENU);
    const isBurgerMenuActive = BurgerMenuOverlay && BurgerMenuOverlay.isActive;
    const shouldShow = useShowSmartBanner('smart');

    const dispatch = useDispatch();
    const ga = useGa();
    const config = useConfig();

    useEffect(() => {
        dispatch(getHeaderBarBannerActionsIfNeeded());
    }, [dispatch]);

    const thunks = [categoryActions.thunk()];
    useFetchingAction(combineThunks(...thunks));

    const iconsCountProps = { minOrderValue, wishListCount };

    const openBurgerMenu = () => {
        pushOverlay(OverlayId.BURGER_MENU);

        ga.oneHeader.trackBurgerMenuIconClick();
    };

    const handleLogoClick = () => {
        ga.oneHeader.trackHeaderAction('logoclick', TrackHeaderActions.HeaderLogoClick);
    };

    useEffect(() => {
        const events = createHeaderEvents(dispatch, config, new Cookies());
        const eventsHandler = EventListener(events);
        return () => eventsHandler.dispose();
    }, [dispatch, config]);

    const { headerMode } = useLayoutByPage();

    return (
        <>
            <DetachableBannerBar detachedHeaderMode={detachedHeaderMode} />
            <OneHeaderContainer className={VictorQaClasses.oneHeader} detachedHeaderMode={detachedHeaderMode}>
                {isBurgerMenuActive && <BurgerFlyout />}
                <OneHeader
                    showHeaderTabs={headerMode === 'default'}
                    handleBurgerMenuIconClick={openBurgerMenu}
                    handleLogoClick={handleLogoClick}
                    {...iconsCountProps}
                />
            </OneHeaderContainer>
            {headerMode === 'mini' && <OneHeaderContainerNonStickyStyle />}
            <StyledShadow isSticky={headerMode === 'default'} smartBanner={shouldShow} />
            {headerMode === 'default' && (
                <DetachableNavBar detachedHeaderMode={detachedHeaderMode} burgerMenuHandler={openBurgerMenu} />
            )}
        </>
    );
};

export default ConnectedOneHeader;
